/**
 * `DimeCloudApp` — the batteries-included cloud terminal: Clerk sign-in
 * (email + enabled socials, e.g. "Continue with Google") gating the full DIME
 * cloud flow (`DimeCloudTerminal`). A host supplies a base terminal config;
 * everything else — sign-in UI, identity exchange, access-code onboarding,
 * gateway-grant resolution, reconnect recovery — ships in the package.
 *
 * **Requires `<DimeTerminalProvider>` above it in the tree.** The provider
 * owns the single `ClerkProvider` instance and distributes the environment /
 * apiUrl context. This prevents the "multiple ClerkProvider" error when two
 * `<DimeCloudApp>` surfaces transition (e.g. drawer → fullscreen route).
 *
 * Two forward-looking seams (inert when omitted):
 *  - `silentExchange`: a non-Clerk DIME-JWT exchange attempted BEFORE any
 *    sign-in UI (the future linked-account fast path — e.g. Paradex JWT →
 *    DIME JWT via the upcoming web-api /auth endpoint). Success ⇒ the flow
 *    runs on it and Clerk never mounts for the session; rejection/failure ⇒
 *    fall through to the Clerk sign-in.
 *  - `renderLinkPrompt`: rendered once after a successful Clerk sign-in so a
 *    host can offer "link your account" (enabling the silent path on future
 *    loads); `dismiss` continues to the terminal.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { Show, SignIn, useAuth, useClerk, useUser } from '@clerk/react';
import styled from 'styled-components';

import type { TerminalConfig } from '../config.js';
import { resolveDimeApiUrl } from './environments.js';
import {
  createClerkTokenExchange,
  type DimeTokenExchange,
} from './identity.js';
import {
  DimeCloudTerminal,
  type CloudTerminalBaseConfig,
  type DimeCloudTerminalProps,
} from './DimeCloudTerminal.js';
import { AuthShell } from './AuthShell.js';
import { useDimeTerminalContext } from './DimeTerminalProvider.js';

type ClerkAppearance = ComponentProps<typeof SignIn>['appearance'];

/**
 * Default dark theme for the Clerk widget, matching the terminal's palette.
 */
const DEFAULT_CLERK_APPEARANCE: ClerkAppearance = {
  variables: {
    colorPrimary: '#7c3aed', // dark purple (buttons, links, focus)
    colorBackground: '#16161a', // card background (dark)
    colorForeground: '#f5f5f5', // primary text
    colorMutedForeground: '#a1a1aa', // muted/secondary text
    colorInput: '#1f1f24', // input field background
    colorInputForeground: '#f5f5f5', // text typed into inputs
    colorPrimaryForeground: '#ffffff', // text on the purple button
    // Neutral drives social-button text/borders; dark themes need a
    // light shade so "Continue with Google" text is readable.
    colorNeutral: '#ffffff',
  },
  elements: {
    // Belt-and-suspenders: force the social button text/border light.
    socialButtonsBlockButton: {
      color: '#f5f5f5',
      borderColor: '#3f3f46',
    },
    socialButtonsBlockButtonText: { color: '#f5f5f5' },
  },
};

/** Host config minus the fields `DimeCloudApp` derives from the session. */
export type CloudAppBaseConfig = Omit<
  CloudTerminalBaseConfig,
  'accountId' | 'onLogout' | 'user'
>;

export interface DimeCloudAppProps {
  readonly clerk?: {
    /** Clerk widget theming; defaults to the packaged dark theme. */
    readonly appearance?: ClerkAppearance;
  };
  /** Host terminal config; connection + session identity fields are derived. */
  readonly config: CloudAppBaseConfig;
  /** Passthrough phase-UI override (see `DimeCloudTerminal`). */
  readonly renderPhase?: DimeCloudTerminalProps['renderPhase'];
  /**
   * Same contract as `TerminalProps.agentEffects`: host nodes rendered inside
   * the agent context (so they can call `useTerminalAgent()` /
   * `useTerminalAuth()`) but outside the visible layout — they should render
   * null into the layout (portals are fine). Forwarded on both the silent
   * fast path and the Clerk path.
   */
  readonly agentEffects?: DimeCloudTerminalProps['agentEffects'];
  /** Same contract as `TerminalProps.compact`: hide the package top bar for space-constrained embeds. */
  readonly compact?: DimeCloudTerminalProps['compact'];
  /**
   * Hide the signed-in email + Log Out bar (`AuthShell`). For embedded hosts
   * whose own chrome frames the terminal — sign-out stays reachable via the
   * terminal sidebar's account menu (`config.onLogout` is still wired).
   * Default false.
   */
  readonly hideAuthShell?: boolean;
  /**
   * FUTURE fast path: a non-Clerk exchange (e.g. Paradex JWT → DIME JWT via
   * the upcoming web-api /auth endpoint) attempted silently before any
   * sign-in UI. Success skips Clerk for the session; rejection/failure falls
   * through to the Clerk sign-in. Omit ⇒ straight to Clerk.
   */
  readonly silentExchange?: {
    readonly exchange: DimeTokenExchange;
    /** Account id for the silent path (no Clerk user to derive one from). */
    readonly accountId: string;
    /** Display identity for the silent path (sidebar account button etc.). */
    readonly user?: TerminalConfig['user'];
    /** "Log Out" action for the silent path. Omit ⇒ the menu hides it. */
    readonly onLogout?: () => void;
  };
  /**
   * FUTURE linking seam: rendered once after a successful Clerk sign-in so a
   * host can offer "link your account" (e.g. to Paradex, enabling the silent
   * fast path on future loads). `dismiss` continues to the terminal. Omit ⇒
   * no prompt.
   */
  readonly renderLinkPrompt?: (ctx: {
    readonly dismiss: () => void;
  }) => ReactNode;
}

type SilentPhase = 'trying' | 'ok' | 'failed';

export function DimeCloudApp({
  clerk,
  config,
  renderPhase,
  agentEffects,
  compact,
  hideAuthShell,
  silentExchange,
  renderLinkPrompt,
}: DimeCloudAppProps): JSX.Element {
  const { environment, apiUrl, isSatellite } = useDimeTerminalContext();

  // One silent attempt per mount; 'failed' when no silent exchange is given
  // so the Clerk path renders immediately with no intermediate state.
  const [silent, setSilent] = useState<SilentPhase>(
    silentExchange != null ? 'trying' : 'failed',
  );
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (silentExchange == null || attemptedRef.current) return;
    attemptedRef.current = true;
    let alive = true;
    const url = resolveDimeApiUrl({ environment, apiUrl });
    silentExchange
      .exchange(url)
      .then(() => {
        if (alive) setSilent('ok');
      })
      .catch(() => {
        if (alive) setSilent('failed');
      });
    return () => {
      alive = false;
    };
  }, [silentExchange, environment, apiUrl]);

  if (silent === 'trying') {
    return (
      <Centered>
        <Muted>Connecting…</Muted>
      </Centered>
    );
  }

  if (silent === 'ok' && silentExchange != null) {
    return (
      <DimeCloudTerminal
        environment={environment}
        apiUrl={apiUrl}
        exchange={silentExchange.exchange}
        renderPhase={renderPhase}
        agentEffects={agentEffects}
        compact={compact}
        config={{
          ...config,
          accountId: silentExchange.accountId,
          user: silentExchange.user,
          onLogout: silentExchange.onLogout,
        }}
      />
    );
  }

  // ClerkProvider is above us in <DimeTerminalProvider> — Show/SignIn/useAuth
  // all find it via context.
  return (
    <>
      <Show when="signed-out">
        {isSatellite ? (
          <SatelliteSignInPrompt />
        ) : (
          <Centered>
            {/* Clerk's prebuilt widget = email + enabled socials in one.
                `virtual` routing keeps the wizard in-place without touching
                the host URL — essential in embedded contexts (drawer/iframe)
                where hash or path routing would hijack the host's location.
                Cast: RoutingStrategy includes 'virtual' but RoutingOptions
                lags behind in @clerk/shared@4 — runtime support is present. */}
            <SignIn
              routing={'virtual' as 'hash'}
              oauthFlow="popup"
              appearance={clerk?.appearance ?? DEFAULT_CLERK_APPEARANCE}
            />
          </Centered>
        )}
      </Show>
      <Show when="signed-in">
        <ClerkSignedIn
          config={config}
          renderPhase={renderPhase}
          agentEffects={agentEffects}
          compact={compact}
          hideAuthShell={hideAuthShell}
          renderLinkPrompt={renderLinkPrompt}
        />
      </Show>
    </>
  );
}

/**
 * Satellite sign-in prompt: instead of rendering the `<SignIn>` widget (which
 * Clerk rejects on satellite domains), show a link that navigates to the
 * primary domain for sign-in. `buildSignInUrl()` appends the current page URL
 * as the redirect target, so Clerk sends the user back after sign-in.
 */
function SatelliteSignInPrompt(): JSX.Element {
  const { buildSignInUrl } = useClerk();
  return (
    <Centered>
      <SignInLink href={buildSignInUrl()}>Sign in to Dime Terminal</SignInLink>
    </Centered>
  );
}

/**
 * Signed-in Clerk path: frame the flow with the auth shell (email + Log Out;
 * suppressed via `hideAuthShell` for embedded hosts with their own chrome)
 * and run the cloud flow on the Clerk exchange. The user never types a
 * gateway URL or token — the grant is resolved server-side and held in memory
 * only, so signing out simply unmounts this subtree and everything is gone.
 */
function ClerkSignedIn({
  config,
  renderPhase,
  agentEffects,
  compact,
  hideAuthShell,
  renderLinkPrompt,
}: Pick<
  DimeCloudAppProps,
  | 'config'
  | 'renderPhase'
  | 'agentEffects'
  | 'compact'
  | 'hideAuthShell'
  | 'renderLinkPrompt'
>): JSX.Element {
  const { environment, apiUrl } = useDimeTerminalContext();
  const { user } = useUser();
  const { signOut, getToken } = useAuth();
  // Pass a no-op callback so Clerk clears the session without navigating.
  // Without the callback, signOut() redirects to afterSignOutUrl (or "/"),
  // which causes a full page reload — fatal in embedded contexts.
  const handleSignOut = useCallback(
    () => void signOut(() => undefined),
    [signOut],
  );
  const exchange = useMemo(
    () => createClerkTokenExchange(getToken),
    [getToken],
  );
  // Clerk profile → sidebar account button + menu header + greeting. Without
  // it the terminal derives a name from the gateway URL, which behind the
  // shared proxy is "gateway." for everyone. Memoized on the scalar fields so
  // the terminal config isn't rebuilt every render.
  const displayName = user?.firstName ?? user?.fullName ?? undefined;
  const email = user?.primaryEmailAddress?.emailAddress;
  const imageUrl = user?.imageUrl;
  const userProfile = useMemo<TerminalConfig['user']>(
    () => ({
      displayName,
      email,
      avatarUrl: imageUrl != null && imageUrl.length > 0 ? imageUrl : undefined,
    }),
    [displayName, email, imageUrl],
  );
  // Link-prompt gate: shown once per signed-in mount, before the terminal.
  const [linkPromptDismissed, setLinkPromptDismissed] = useState(false);
  const dismissLinkPrompt = useCallback(() => setLinkPromptDismissed(true), []);

  const body =
    user == null ? (
      <Centered>
        <Muted>Loading…</Muted>
      </Centered>
    ) : renderLinkPrompt != null && !linkPromptDismissed ? (
      <>{renderLinkPrompt({ dismiss: dismissLinkPrompt })}</>
    ) : (
      <DimeCloudTerminal
        environment={environment}
        apiUrl={apiUrl}
        exchange={exchange}
        renderPhase={renderPhase}
        agentEffects={agentEffects}
        compact={compact}
        config={{
          ...config,
          accountId: user.id,
          user: userProfile,
          onLogout: handleSignOut,
        }}
      />
    );

  if (hideAuthShell === true) return body;
  return (
    <AuthShell email={email} onLogout={handleSignOut}>
      {body}
    </AuthShell>
  );
}

const Centered = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
`;

const Muted = styled.span`
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const SignInLink = styled.a`
  font-size: 0.9rem;
  color: inherit;
  text-decoration: none;
  padding: 8px 16px;
  border: 1px solid currentColor;
  border-radius: 6px;
  opacity: 0.7;
  cursor: pointer;
  &:hover {
    opacity: 1;
  }
`;
